import ExcelJS from 'exceljs';

const normalizeText = (value) => String(value ?? '').trim();

const normalizeSheetProductId = (value) => normalizeText(value).replace(/\.0+$/, '');

const normalizeHeader = (value) => normalizeText(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, '')
  .toUpperCase();

const REQUIRED_HEADERS = {
  ID: 'ID',
  NOME: 'NOME',
  PRECO: 'PRECO',
};

const getCellText = (row, column) => normalizeText(row.getCell(column).text || row.getCell(column).value);

const parsePriceToCents = (input) => {
  const value = normalizeText(input);
  if (!value) return null;

  let normalized = value
    .replace(/\s/g, '')
    .replace('R$', '')
    .replace(/[^0-9.,-]/g, '');

  if (!normalized) return null;

  if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    const parts = normalized.split('.');
    if (parts.length > 2) {
      normalized = `${parts.slice(0, -1).join('')}.${parts[parts.length - 1]}`;
    }
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
};

const readRowsFile = async (file) => {
  const workbook = new ExcelJS.Workbook();
  const fileBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(fileBuffer);

  const worksheet = workbook.worksheets[0];
  if (!worksheet) throw new Error('A planilha não possui nenhuma aba.');

  const headerRow = worksheet.getRow(1);
  let idColumn = null;
  let nameColumn = null;
  let priceColumn = null;

  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const headerValue = normalizeHeader(cell.text || cell.value);
    if (headerValue === REQUIRED_HEADERS.ID) idColumn = colNumber;
    if (headerValue === REQUIRED_HEADERS.NOME) nameColumn = colNumber;
    if (headerValue === REQUIRED_HEADERS.PRECO) priceColumn = colNumber;
  });

  if (!idColumn || !nameColumn || !priceColumn) {
    throw new Error('Cabeçalho inválido. Use ID, NOME e PREÇO na primeira linha.');
  }

  const sheetRows = [];

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;

    const idText = normalizeSheetProductId(getCellText(row, idColumn));
    const nameText = getCellText(row, nameColumn);
    const priceText = getCellText(row, priceColumn);
    if (!idText && !nameText && !priceText) return;

    sheetRows.push({
      rowNumber,
      idFromSheet: idText,
      nameFromSheet: nameText,
      priceFromSheet: Number(priceText),
    });
  });

  return sheetRows;
};

const buildPricePreview = (sheetRows, products) => {
  const productsById = new Map(products.map((item) => [item.id, item]));
  const updateMap = new Map();
  const invalidRows = [];

  sheetRows.forEach((row) => {
    const sheetId = normalizeSheetProductId(row.idFromSheet);
    if (!sheetId) {
      invalidRows.push({ rowNumber: row.rowNumber, reason: 'ID vazio' });
      return;
    }

    const product = productsById.get(sheetId);
    if (!product) {
      invalidRows.push({ rowNumber: row.rowNumber, reason: `Produto não encontrado (${sheetId})` });
      return;
    }

    const newPrice = parsePriceToCents(row.priceFromSheet);
    if (newPrice === null) {
      invalidRows.push({ rowNumber: row.rowNumber, reason: `Preço inválido para ${sheetId}` });
      return;
    }

    updateMap.set(product.id, {
      id: product.id,
      idFromSheet: sheetId,
      name: product.name || row.nameFromSheet || '-',
      image_url: product.image_url || '',
      active: product.active,
      oldPrice: product.price,
      newPrice,
      storeProdExist: !!product.storeProdExist,
    });
  });

  return {
    updates: Array.from(updateMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    invalidRows,
  };
};

export {
  buildPricePreview,
  readRowsFile,
};
